import qs from 'qs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createStyles } from '@mantine/core';
import { useRouter } from 'next/router';

import type { StaticFieldProps } from '../../types/common';
import { getFilteredSelectOptions } from '../utils/getFilteredSelectOptions';
import { FieldMeta } from '../common/FieldMeta';
import SelectFieldOptions from '../common/SelectFieldOptions';
import autoScrollToNextField from '../utils/autoScrollToNextField';

import type { SingleSelectField } from './types';

const useStyles = createStyles(() => ({
  container: {
    position: 'relative',
  },
}));

export function SingleSelectFieldElement({
  field,
  path,
  propagateFieldLabelToParent,
  value,
  onValueChange,
  onPreserveSections,
  errors,
  isTouched,
  sectionKey,
  allValues,
  onGoToSection,
  onNextSectionClick,
  allErrors,
}: StaticFieldProps<SingleSelectField>) {
  const ref = useRef<HTMLDivElement>();
  const { classes } = useStyles();
  const router = useRouter();
  const currentQuery = router.query;
  const initialValues = useMemo<
    Partial<{ stepOne: { leagueId: string; contestTemplateId: string } }>
  >(() => qs.parse(qs.stringify(router.query, { encode: false })), [router.query]);

  const options = getFilteredSelectOptions({
    options: field.options,
    sectionKey,
    allValues,
    allErrors,
  });

  useEffect(() => {
    if (initialValues?.[sectionKey]?.[field.key]) {
      autoScrollToNextField(field?.scrollToNextSection, ref?.current);
    }
  }, [field.key, field?.scrollToNextSection, initialValues, sectionKey]);

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;
      const targetOption = options.find((option) => `${option.value}` === `${target.value}`);
      if (!targetOption) {
        return;
      }

      if (field?.preserveSectionKeys) {
        onPreserveSections(field.preserveSectionKeys);
      }

      if (field?.autoFillSteps) {
        currentQuery.status = 'inprogress';

        router.push({
          pathname: router.pathname,
          query: currentQuery,
        });
        onNextSectionClick();
      }

      if (field?.goToNextSection) {
        onGoToSection(field?.goToNextSection);
        return;
      }

      autoScrollToNextField(field?.scrollToNextSection, ref?.current);
      onValueChange(targetOption.value);
    },
    [
      options,
      field?.preserveSectionKeys,
      onPreserveSections,
      field?.autoFillSteps,
      field?.goToNextSection,
      field?.scrollToNextSection,
      onValueChange,
      currentQuery,
      router,
      onNextSectionClick,
      onGoToSection,
    ]
  );

  useEffect(() => {
    if (!value && !field?.defaultValue) return;
    if (options.find((option) => option.value === value)) return;

    onValueChange(field?.defaultValue || null, true);
  }, [field?.defaultValue, onValueChange, options, value]);

  // This is here just to prevent rerenders since this function is being
  // recreated on every render
  const isPropagateFieldLabelToParent = typeof propagateFieldLabelToParent === 'function';
  useEffect(() => {
    if (!isPropagateFieldLabelToParent) return;

    const selectedOption = options.find((option) => option.value === value);
    if (typeof selectedOption?.overrideLabel === 'string') {
      propagateFieldLabelToParent(
        path,
        selectedOption.doNotPropagate ? undefined : selectedOption.overrideLabel
      );
    } else if (typeof selectedOption?.label === 'string') {
      propagateFieldLabelToParent(
        path,
        selectedOption.doNotPropagate ? undefined : selectedOption.label
      );
    }

    return () => {
      propagateFieldLabelToParent(path, undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPropagateFieldLabelToParent, value]);

  if (options.length === 0) {
    return null;
  }

  return (
    <div className={classes.container} ref={ref}>
      <FieldMeta info={field?.info} field={field} errors={errors} isTouched={isTouched} />
      <SelectFieldOptions
        field={field}
        options={options}
        value={value as string}
        onChange={handleOnChange}
      />
    </div>
  );
}
